import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore, useReadOnly, READ_ONLY_TITLE } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useWinningTopic } from "@/hooks/useWinningTopic";
import type { Output, RevealState } from "@offsite/shared";

interface TeamMeta {
  teamNumber: number;
  teamName: string | null;
}

interface Props {
  /** "overnight" while Sonnet is still building; "reveal" once all done. */
  phase: "overnight" | "reveal";
}

/**
 * Unified carousel for overnight (Sonnet building) + reveal (Tag-2 showcase).
 * 2-stufiger Reveal: Klick aufs Team-Card öffnet die Meta-Übersicht (Format,
 * Größe, Modell). Erst der „Output zeigen"-Button enthüllt den eigentlichen
 * Inhalt. Reveal-Flag persistiert in DDB so Reviewer-Magic-Link denselben
 * Stand sieht.
 */
export function OutputCarousel({ phase }: Props) {
  // AR: eine Person pro Team — der Name führt statt der Nummer.
  const { soloTeams, stationsAblauf } = useOffsiteConfig();
  // Strategietag: Team-Reiter an die Seite und maximale Fläche für den
  // Prototyp — das 68vh-Fenster war auf der Leinwand zu klein (Wunsch 17.09.).
  const stationsLayout = stationsAblauf !== undefined;
  const { state } = useStore();
  const readOnly = useReadOnly();
  const winner = useWinningTopic();
  const token = state.auth.token;

  const [teams, setTeams] = useState<TeamMeta[]>([]);
  const [outputs, setOutputs] = useState<Output[]>([]);
  const [reveal, setReveal] = useState<RevealState>({ eval: {}, output: {} });
  const [activeTeam, setActiveTeam] = useState<number | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [t, o, rv] = await Promise.all([
          api.teams.list(token),
          api.overnight.outputs(token),
          api.reveal.get(token),
        ]);
        if (cancelled) return;
        // Nur Teams des laufenden Durchgangs: `teams.list` liefert alles, was in
        // der Tabelle liegt, `overnight/outputs` ist auf die laufende
        // Besuchergruppe gefiltert. Auf der Leinwand standen sonst neben dem
        // eigenen Ergebnis noch vier Reiter fremder Teams — mit leerem Inhalt,
        // weil für die nie etwas gebaut wurde.
        const derRunde = new Set(o.outputs.map((x) => x.teamNumber));
        setTeams(
          t.teams
            .filter((x) => derRunde.size === 0 || derRunde.has(x.number))
            .map((x) => ({ teamNumber: x.number, teamName: x.name })),
        );
        setOutputs(o.outputs);
        setReveal(rv.reveal);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, phase === "overnight" ? 4000 : 6000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, phase]);

  useEffect(() => {
    if (activeTeam !== null) return;
    if (outputs.length > 0) setActiveTeam(outputs[0].teamNumber);
    else if (teams.length > 0) setActiveTeam(teams[0].teamNumber);
  }, [outputs, teams, activeTeam]);

  const outputByTeam = new Map(outputs.map((o) => [o.teamNumber, o]));
  const doneCount = outputs.length;
  const total = teams.length;
  const allDone = total > 0 && doneCount >= total;

  const activeMeta = teams.find((t) => t.teamNumber === activeTeam) ?? null;
  const activeOutput =
    activeTeam !== null ? outputByTeam.get(activeTeam) ?? null : null;
  const activeRevealed =
    activeTeam !== null
      ? reveal.output[activeTeam]?.revealed ?? false
      : false;

  const toggleReveal = async (teamNumber: number, revealed: boolean) => {
    if (readOnly) return;
    setReveal((r) => ({
      ...r,
      output: { ...r.output, [teamNumber]: { revealed } },
    }));
    try {
      const res = await api.reveal.setOutput(token, teamNumber, revealed);
      setReveal((r) => ({
        ...r,
        output: { ...r.output, [teamNumber]: res.output },
      }));
    } catch {
      setReveal((r) => ({
        ...r,
        output: { ...r.output, [teamNumber]: { revealed: !revealed } },
      }));
    }
  };

  /** Click on a "done" card: select team + auto-reveal output (one-way). */
  const handleCardClick = (teamNumber: number, hasOutput: boolean) => {
    setActiveTeam(teamNumber);
    if (!hasOutput || readOnly) return;
    const cur = reveal.output[teamNumber]?.revealed ?? false;
    if (!cur) void toggleReveal(teamNumber, true);
  };

  /** Toggle every output reveal flag at once (bored-audience escape hatch). */
  const masterReveal = async (target: boolean) => {
    if (readOnly) return;
    const targets = outputs.map((o) => o.teamNumber);
    setReveal((r) => {
      const next = { ...r.output };
      for (const n of targets) next[n] = { revealed: target };
      return { ...r, output: next };
    });
    await Promise.all(
      targets.map((n) =>
        api.reveal.setOutput(token, n, target).catch(() => undefined),
      ),
    );
  };

  const allRevealed =
    outputs.length > 0 &&
    outputs.every((o) => reveal.output[o.teamNumber]?.revealed);

  return (
    <div
      className={`eval-board${stationsLayout ? " is-stationslayout" : ""}`}
      data-testid="output-carousel"
    >
      <div className="deck-bg" aria-hidden="true" />

      <header className="eval-board-head">
        <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
          {phase === "overnight"
            ? "Workshop · KI Umsetzung läuft"
            : "Workshop · KI hat geliefert"}
        </div>
        {winner && (
          <div className="eval-board-topic">
            Thema: <strong>{winner.topic.title}</strong>
          </div>
        )}
        <h1 className="deck-title eval-board-title">
          {phase === "overnight"
            ? allDone
              ? "Alle Prototypen fertig"
              : `${doneCount} / ${total || "…"} Prototypen fertig`
            : "Was hat die KI gebaut?"}
        </h1>
        {outputs.length > 0 && !readOnly && (
          <button
            type="button"
            className="reveal-master-btn"
            onClick={() => masterReveal(!allRevealed)}
            title={
              allRevealed
                ? "Alle Outputs wieder verbergen"
                : "Alle Outputs aller Teams enthüllen"
            }
          >
            {allRevealed ? "Alles verbergen" : "Alles enthüllen"}
          </button>
        )}
      </header>

      {/* Compact team-tab row — only when multiple teams. With one team
       * the detail panel below is sufficient (team-name + topic already shown
       * there, no need for a separate header card). */}
      {teams.length > 1 && (
        <div className="output-team-tabs">
          {teams.map((t) => {
            const out = outputByTeam.get(t.teamNumber);
            const isActive = activeTeam === t.teamNumber;
            const isDone = !!out;
            const isRevealedTab = !!reveal.output[t.teamNumber]?.revealed;
            return (
              <button
                key={t.teamNumber}
                type="button"
                // Test-Hook: nur Karten mit fertigem Prototyp. Ein Team ohne
                // Output ist noch keine Prototyp-Karte — der E2E-Test zählt
                // hierüber, wie viele Prototypen tatsächlich da sind.
                data-testid={isDone ? "output-card" : undefined}
                className={`output-team-tab${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}${isRevealedTab ? " is-revealed" : ""}`}
                onClick={() => handleCardClick(t.teamNumber, isDone)}
              >
                <span className="output-team-tab-num">{t.teamNumber}</span>
                <span className="output-team-tab-name">
                  {t.teamName ?? `Team ${t.teamNumber}`}
                </span>
                {isDone ? (
                  <span className="output-team-tab-status" aria-hidden="true">
                    ✓
                  </span>
                ) : phase === "overnight" ? (
                  <span
                    className="spinner"
                    style={{ width: 12, height: 12 }}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {/* Detail panel — Stage 1: meta visible, Stage 2: content reveal */}
      <div className="reveal-detail">
        {activeOutput ? (
          <OutputDetail
            output={activeOutput}
            revealed={activeRevealed}
            readOnly={readOnly}
            anzeigeName={activeMeta?.teamName ?? activeOutput.teamName ?? null}
            soloTeams={soloTeams === true}
            stationsLayout={stationsLayout}
            onToggle={() =>
              toggleReveal(activeOutput.teamNumber, !activeRevealed)
            }
          />
        ) : activeMeta ? (
          <div className="reveal-detail-pending">
            <div className="eyebrow">
              {activeMeta.teamName ?? `Team ${activeMeta.teamNumber}`}
            </div>
            <h3 style={{ color: "var(--svo-weiss)", margin: "8px 0 0" }}>
              {phase === "overnight"
                ? "Sonnet 4.6 baut gerade …"
                : "Noch kein Output"}
            </h3>
            <p
              className="form-hint"
              style={{ color: "rgba(255,255,255,0.55)", marginTop: 12 }}
            >
              Sobald fertig, erscheint hier eine Meta-Übersicht — der Output
              selbst wird auf Klick enthüllt.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OutputDetail({
  output,
  revealed,
  readOnly,
  onToggle,
  anzeigeName,
  soloTeams,
  stationsLayout,
}: {
  output: Output;
  revealed: boolean;
  readOnly: boolean;
  onToggle: () => void;
  /** Name des Teams laut aktuellem Stand — im Output ist er eingefroren und
   *  fehlt bei vorbereiteten Notfall-Ergebnissen ganz. */
  anzeigeName: string | null;
  /** Solo-Offsite (AR): eine Person pro Team, der Name führt statt der Nummer. */
  soloTeams: boolean;
  /** Strategietag: Reiter seitlich, Prototyp-Fenster so hoch wie möglich. */
  stationsLayout?: boolean;
}) {
  const inhaltMinHoehe = stationsLayout ? "80vh" : "68vh";
  return (
    <div className="reveal-detail-inner">
      <div className="reveal-detail-head">
        <div className="eyebrow">
          {soloTeams
            ? (anzeigeName ?? `Team ${output.teamNumber}`)
            : `Team ${output.teamNumber}${anzeigeName ? ` · ${anzeigeName}` : ""}`}
        </div>
        <h3 style={{ color: "var(--svo-weiss)", margin: "6px 0 0" }}>
          {output.topicTitle}
        </h3>
      </div>

      {/* Output content with reveal-button overlay */}
      <div className="reveal-section">
        <div
          className={`reveal-section-content${revealed ? "" : " reveal-blur"}`}
          aria-hidden={!revealed}
          style={{
            background: "#fff",
            borderRadius: "var(--radius-2xl)",
            overflow: "auto",
            display: "flex",
            minHeight: inhaltMinHoehe,
          }}
        >
          {output.contentType === "html" ? (
            <iframe
              title={`Output Team ${output.teamNumber}`}
              srcDoc={output.content}
              sandbox="allow-scripts"
              referrerPolicy="no-referrer"
              loading="lazy"
              style={{ width: "100%", border: "none", minHeight: inhaltMinHoehe }}
            />
          ) : (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                padding: 24,
                margin: 0,
                width: "100%",
                color: "#0a0a0a",
              }}
            >
              {output.content}
            </pre>
          )}
        </div>
        {!revealed && (
          <button
            type="button"
            className="reveal-section-btn"
            onClick={onToggle}
            disabled={readOnly}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            Output zeigen
          </button>
        )}
        {revealed && !readOnly && (
          <button
            type="button"
            className="reveal-section-hide"
            onClick={onToggle}
            title="wieder verbergen"
          >
            verbergen
          </button>
        )}
      </div>
    </div>
  );
}
